// The approver's side of an attendance appeal.
//
// Renders nothing at all when there is nothing to decide OR to look back on, so
// it costs a manager with an empty queue one line of nothing on the Attendance
// page. When it does render it leads with the three facts a decision turns on
// — which day, what the record says now, and what they are asking it to say —
// because approving here CHANGES the record, and nobody should be able to do
// that without having seen the before and the after side by side.
//
// Declining takes the same optional note as approving. A decline with no word
// back is the version of this feature people hate.
//
// Two tabs, not one list. Until now this fetched status=PENDING only, so the
// moment an appeal was settled it vanished from the approver's side entirely:
// an approval REWRITES somebody's attendance record, and there was nowhere to
// go and see that it had happened, who did it, or what was said.
//
// The two tabs are two fetches, not one. History used to ride along with the
// queue and then rendered every settled appeal of the last 90 days as a full
// card in one column — unreadable by the second month, and no way to find a
// particular day. It is now filtered (employee / month / type) and paged on the
// SERVER, which is the only side that can reach a row this page does not hold,
// and each row opens in a dialog instead of spending ten lines inline.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Gavel, History, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  decideAttendanceAppeal,
  listAttendanceAppeals,
  type AttendanceAppealPerson,
  type AttendanceAppealRow,
  type AttendanceAppealType,
  type AttendanceAppealTypeSpec,
} from '../../api/attendance';
import { useToast } from '../../hooks/useToast';
import PersonAvatar from '../people/PersonAvatar';

interface Props {
  /** Bump to re-fetch (a decision elsewhere, a new appeal, a reload). */
  reloadKey?: number;
  /** Told after a decision, so the page can refresh the day it just changed. */
  onDecided?: (row: AttendanceAppealRow, decision: 'approve' | 'reject') => void;
}

/** History rows per page. Small on purpose: this sits inside the Attendance
 *  page, under the queue, and is read a page at a time rather than scrolled. */
const PAGE_SIZE = 8;

/** How far back the month filter offers to look. */
const MONTH_CHOICES = 12;

const dayOf = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString([], { day: '2-digit', month: 'short' });
};

/** "23 Sep" for one day, "23 Sep – 25 Sep" when the appeal names a span. */
const spanOf = (row: AttendanceAppealRow) =>
  row.to_date && row.to_date !== row.on_date
    ? `${dayOf(row.on_date)} – ${dayOf(row.to_date)}`
    : dayOf(row.on_date);

/** "08:59 → 18:00" / "not recorded" — what the day says today. */
const observed = (row: AttendanceAppealRow) => {
  if (!row.observed_clock_in && !row.observed_clock_out) return 'not recorded';
  return `${row.observed_clock_in || '—'} → ${row.observed_clock_out || '—'}`;
};

/** "22 Sep, 04:30 pm" — when the decision was actually taken. */
const decidedAt = (iso: string | null) => {
  if (!iso) return '';
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? ''
    : at.toLocaleString([], {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const PAST_STYLE: Record<string, string> = {
  APPROVED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200',
  REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-200',
  CANCELLED: 'bg-gray-100 text-muted-foreground',
};

const PAST_LABEL: Record<string, string> = {
  APPROVED: 'Approved',
  REJECTED: 'Declined',
  CANCELLED: 'Withdrawn',
};

/** The last 12 months as { value: "2026-09", label: "Sep 2026" }. */
const monthOptions = (from = new Date()) =>
  Array.from({ length: MONTH_CHOICES }, (_, back) => {
    // Day 1 so stepping back a month can never skip one (31 Mar - 1 month).
    const d = new Date(from.getFullYear(), from.getMonth() - back, 1);
    return {
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString([], { month: 'short', year: 'numeric' }),
    };
  });

const selectCls =
  'rounded-md border border-input bg-card px-2 py-1 text-xs text-foreground outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500';

export default function AppealApprovals({ reloadKey = 0, onDecided }: Props) {
  const [waiting, setWaiting] = useState<AttendanceAppealRow[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [tab, setTab] = useState<'waiting' | 'history'>('waiting');

  // History: its own rows, its own filters, its own page.
  const [history, setHistory] = useState<AttendanceAppealRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [people, setPeople] = useState<AttendanceAppealPerson[]>([]);
  const [types, setTypes] = useState<AttendanceAppealTypeSpec[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [month, setMonth] = useState('');
  const [appealType, setAppealType] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [detail, setDetail] = useState<AttendanceAppealRow | null>(null);
  const [loaded, setLoaded] = useState(false);

  const { toast } = useToast();
  const months = useMemo(() => monthOptions(), []);
  const filtered = Boolean(employeeId || month || appealType);

  const loadWaiting = useCallback(async () => {
    try {
      const { appeals, types: specs } = await listAttendanceAppeals({
        scope: 'approvals',
        status: 'PENDING',
        days: 90,
      });
      setWaiting(appeals);
      if (specs?.length) setTypes(specs);
    } catch {
      // An approvals queue that cannot load must not take the attendance page
      // down with it — the appeals are still on the bell and in the list.
      setWaiting([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const result = await listAttendanceAppeals({
        scope: 'approvals',
        status: 'SETTLED',
        // A month filter names its own window; otherwise the rolling 90 days.
        ...(month ? { month } : { days: 90 }),
        ...(employeeId ? { employeeId } : {}),
        ...(appealType ? { appealType: appealType as AttendanceAppealType } : {}),
        page,
        pageSize: PAGE_SIZE,
      });
      setHistory(result.appeals);
      // Optional-chained throughout: an older server (or a stubbed client)
      // answering without the paging fields must not take the whole history
      // down with a TypeError — it just means one page of what it did send.
      setTotal(result.total ?? result.appeals.length);
      // The dropdown is the server's list of everyone in the window, so it
      // holds still instead of being rebuilt from the current page.
      if (result.employees?.length) setPeople(result.employees);
      if (result.types?.length) setTypes(result.types);
    } catch {
      setHistory([]);
      setTotal(0);
    } finally {
      setLoadingHistory(false);
    }
  }, [appealType, employeeId, month, page]);

  useEffect(() => {
    loadWaiting();
  }, [loadWaiting, reloadKey]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, reloadKey]);

  // Any filter change starts again from the first page: page 3 of the old
  // result set is not page 3 of the new one.
  const refilter = (apply: () => void) => {
    apply();
    setPage(1);
  };

  const decide = async (
    row: AttendanceAppealRow,
    decision: 'approve' | 'reject',
  ) => {
    setBusyId(row.id);
    try {
      const decided = await decideAttendanceAppeal(row.id, decision, notes[row.id]);
      // Settle it locally rather than re-fetching: it leaves the queue either
      // way, and the list must not flicker back in while the call lands. It
      // moves to History rather than disappearing — that is the whole point of
      // the second tab, and putting it there by hand means the move shows even
      // on a filter the new row would not match.
      setWaiting((current) => current.filter((r) => r.id !== row.id));
      setHistory((current) => [decided || { ...row, status: 'APPROVED' }, ...current]);
      setTotal((current) => current + 1);
      toast({
        title: decision === 'approve' ? 'Approved' : 'Declined',
        description:
          decision === 'approve'
            ? // Quote what actually changed. "Approved" alone leaves the
              // approver unsure whether the timesheet moved, which is exactly
              // the doubt this feature exists to remove.
              decided?.applied_note ||
              `${row.employee_name || 'They'} have been told.`
            : `${row.employee_name || 'They'} have been told.`,
      });
      onDecided?.(decided || row, decision);
    } catch (err: any) {
      toast({
        title: "Couldn't record that",
        description: err?.message || 'Please try again.',
        variant: 'destructive',
      });
      // A 409 means the other approver got there first. Re-fetch rather than
      // guessing: we do not know what they decided, and History has to show
      // their answer, not a row we quietly dropped.
      if (/already/i.test(err?.message || '')) {
        loadWaiting();
        loadHistory();
      }
    } finally {
      setBusyId(null);
    }
  };

  // Nothing has ever been raised here. Stay off the page entirely — but never
  // while a filter is on, or clearing it would be impossible.
  if (loaded && waiting.length === 0 && total === 0 && !filtered) return null;

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstOnPage = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastOnPage = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 shadow-sm dark:border-emerald-900 dark:bg-emerald-950/30">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Gavel className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
        <h3 className="text-sm font-semibold text-foreground">
          Attendance appeals
        </h3>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => setTab('waiting')}
            className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
              tab === 'waiting'
                ? 'bg-emerald-600 text-white'
                : 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100'
            }`}
          >
            {waiting.length} waiting
          </button>
          <button
            type="button"
            onClick={() => setTab('history')}
            className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
              tab === 'history'
                ? 'bg-emerald-600 text-white'
                : 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100'
            }`}
          >
            <History className="h-3 w-3" /> History ({total})
          </button>
        </div>
      </div>

      {tab === 'history' ? (
        <>
          {/* Filters. Employee and month are how anyone actually looks for a
              past appeal; type separates "was this a WFH claim" from the
              timing corrections it would otherwise be buried in. */}
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <select
              aria-label="Filter by employee"
              value={employeeId}
              onChange={(e) => refilter(() => setEmployeeId(e.target.value))}
              className={selectCls}
            >
              <option value="">All employees</option>
              {people.map((person) => (
                <option
                  key={person.user_id || person.employee_id}
                  value={person.user_id || person.employee_id}
                >
                  {person.name}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by month"
              value={month}
              onChange={(e) => refilter(() => setMonth(e.target.value))}
              className={selectCls}
            >
              <option value="">Last 90 days</option>
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by appeal type"
              value={appealType}
              onChange={(e) => refilter(() => setAppealType(e.target.value))}
              className={selectCls}
            >
              <option value="">All types</option>
              {types.map((spec) => (
                <option key={spec.key} value={spec.key}>
                  {spec.label}
                </option>
              ))}
            </select>
            {filtered && (
              <button
                type="button"
                onClick={() =>
                  refilter(() => {
                    setEmployeeId('');
                    setMonth('');
                    setAppealType('');
                  })
                }
                className="text-[11px] font-semibold text-emerald-700 underline dark:text-emerald-400"
              >
                Clear
              </button>
            )}
          </div>

          {loadingHistory && history.length === 0 ? (
            <p className="py-2 text-xs text-muted-foreground">Loading…</p>
          ) : history.length === 0 ? (
            <p className="py-2 text-xs text-muted-foreground">
              {filtered
                ? 'No decided appeals match these filters.'
                : 'Nothing has been decided in the last 90 days.'}
            </p>
          ) : (
            <ul className="space-y-1.5">
              {history.map((row) => (
                <li key={row.id}>
                  {/* One line per appeal, the whole of it a button. Everything
                      a decision was based on is in the dialog — it does not
                      belong in a list anyone has to scroll past. */}
                  <button
                    type="button"
                    onClick={() => setDetail(row)}
                    className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-border bg-card p-2.5 text-left hover:border-emerald-300 hover:bg-emerald-50/50 dark:hover:border-emerald-800 dark:hover:bg-emerald-950/30"
                  >
                    <PersonAvatar
                      userId={row.user_id}
                      employeeId={row.employee_id}
                      name={row.employee_name}
                      size={24}
                    />
                    <span className="text-sm font-semibold text-foreground">
                      {row.employee_name || 'An employee'}
                    </span>
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-foreground">
                      {row.appeal_type_label}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {spanOf(row)}
                    </span>
                    <span
                      className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        PAST_STYLE[row.status] || PAST_STYLE.CANCELLED
                      }`}
                    >
                      {PAST_LABEL[row.status] || row.status}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {total > PAGE_SIZE && (
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="text-[11px] text-muted-foreground">
                {firstOnPage}–{lastOnPage} of {total}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={page <= 1 || loadingHistory}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  className="rounded-md border border-border p-1 disabled:opacity-40"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <span className="text-[11px] text-muted-foreground">
                  {page} / {pages}
                </span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={page >= pages || loadingHistory}
                  onClick={() => setPage((current) => Math.min(pages, current + 1))}
                  className="rounded-md border border-border p-1 disabled:opacity-40"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : waiting.length === 0 ? (
        <p className="py-2 text-xs text-muted-foreground">
          Nothing is waiting on your decision.
        </p>
      ) : (
      <ul className="space-y-2">
        {waiting.map((row) => (
          <li
            key={row.id}
            className="rounded-lg border border-emerald-200 bg-card p-3 dark:border-emerald-900"
          >
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="inline-flex items-center gap-2 self-center">
                <PersonAvatar
                  userId={row.user_id}
                  employeeId={row.employee_id}
                  name={row.employee_name}
                  size={28}
                />
                <span className="text-sm font-semibold text-foreground">
                  {row.employee_name || 'An employee'}
                </span>
              </span>
              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
                {row.appeal_type_label}
              </span>
              <span className="text-xs text-muted-foreground">
                for {spanOf(row)}
              </span>
            </div>

            {/* Before and after, on one line. This is the whole basis of the
                decision, so it is never behind a click. */}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className="text-muted-foreground">
                Now: <span className="font-mono">{observed(row)}</span>
              </span>
              <span className="font-medium text-emerald-700 dark:text-emerald-400">{row.ask}</span>
            </div>

            <p className="mt-1.5 text-sm text-foreground">
              {row.reason || <span className="text-muted-foreground">No reason given</span>}
            </p>

            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                value={notes[row.id] || ''}
                onChange={(e) =>
                  setNotes((current) => ({ ...current, [row.id]: e.target.value }))
                }
                placeholder="Note back (optional)"
                maxLength={500}
                className="flex-1 rounded-md border border-input px-2 py-1.5 text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => decide(row, 'approve')}
                  disabled={busyId === row.id}
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  title="Approving adjusts this day's attendance record"
                >
                  <Check className="mr-1 h-3.5 w-3.5" /> Approve &amp; adjust
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => decide(row, 'reject')}
                  disabled={busyId === row.id}
                  className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/40"
                >
                  <X className="mr-1 h-3.5 w-3.5" /> Decline
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ul>
      )}

      {/* The settled appeal in full. Everything the list dropped lives here:
          what the record said, what was asked, why, and who answered — an
          approval rewrote somebody's attendance, so the trail has to be
          readable somewhere. */}
      <Dialog open={detail !== null} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-lg bg-card">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
                  <PersonAvatar
                    userId={detail.user_id}
                    employeeId={detail.employee_id}
                    name={detail.employee_name}
                    size={28}
                  />
                  {detail.employee_name || 'An employee'}
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      PAST_STYLE[detail.status] || PAST_STYLE.CANCELLED
                    }`}
                  >
                    {PAST_LABEL[detail.status] || detail.status}
                  </span>
                </DialogTitle>
              </DialogHeader>

              <dl className="space-y-2.5 text-sm">
                <div className="flex gap-2">
                  <dt className="w-28 shrink-0 text-xs text-muted-foreground">Type</dt>
                  <dd className="text-foreground">{detail.appeal_type_label}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-28 shrink-0 text-xs text-muted-foreground">
                    {detail.to_date && detail.to_date !== detail.on_date ? 'Days' : 'Day'}
                  </dt>
                  <dd className="text-foreground">{spanOf(detail)}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-28 shrink-0 text-xs text-muted-foreground">Record said</dt>
                  <dd className="font-mono text-foreground">{observed(detail)}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-28 shrink-0 text-xs text-muted-foreground">Asked for</dt>
                  <dd className="text-emerald-700 dark:text-emerald-400">{detail.ask}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-28 shrink-0 text-xs text-muted-foreground">Reason</dt>
                  <dd className="text-foreground">
                    {detail.reason || (
                      <span className="text-muted-foreground">No reason given</span>
                    )}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-28 shrink-0 text-xs text-muted-foreground">Decided by</dt>
                  <dd className="flex items-center gap-1.5 text-foreground">
                    {detail.status !== 'CANCELLED' && (
                      // decided_by is the user who actually answered — the one
                      // decided_by_name names. approver_user_id is only whoever
                      // the appeal was routed to first, so it would show the
                      // wrong face.
                      <PersonAvatar
                        userId={detail.decided_by}
                        name={detail.decided_by_name}
                        size={20}
                      />
                    )}
                    {detail.status === 'CANCELLED'
                      ? 'Withdrawn by the employee'
                      : `${detail.decided_by_name || 'An approver'}${
                          decidedAt(detail.decided_at)
                            ? ` · ${decidedAt(detail.decided_at)}`
                            : ''
                        }`}
                  </dd>
                </div>
              </dl>

              {detail.applied_note && (
                <p className="flex items-start gap-1.5 rounded-lg bg-emerald-50 p-2 text-xs text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                  <Check className="mt-0.5 h-3 w-3 shrink-0" />
                  {detail.applied_note}
                </p>
              )}
              {detail.decision_note && (
                <p className="text-xs italic text-muted-foreground">
                  “{detail.decision_note}”
                </p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
